// PhotoPicker shows what the customer actually picked.
//
// A bare <input type="file" multiple> tells you "3 archivos" and nothing else —
// you cannot see whether you attached the right photos, or drop the one you
// picked by mistake. This renders a thumbnail per file, lets you remove any of
// them before sending, and enforces the size limit the hint promises instead of
// letting the upload fail on the server.
//
// Island Architecture: a light-DOM web component, so the <input> it wraps still
// takes part in the surrounding <form> submission.

const MAX_BYTES = 8 * 1024 * 1024; // "hasta 8 MB c/u" — the hint, made real
const MAX_FILES = 8;

export class PhotoPicker extends HTMLElement {
  private input!: HTMLInputElement;
  private grid!: HTMLElement;
  private note!: HTMLElement;
  /** The files we intend to submit. A FileList is read-only, so we keep our own. */
  private files: File[] = [];

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement>("[data-photo-input]");
    const grid = this.querySelector<HTMLElement>("[data-photo-grid]");
    const note = this.querySelector<HTMLElement>("[data-photo-note]");
    if (!input || !grid || !note) return;

    this.input = input;
    this.grid = grid;
    this.note = note;

    this.input.addEventListener("change", (): void => {
      this.add(Array.from(this.input.files ?? []));
    });

    // Dropping photos straight onto the box is the obvious gesture.
    const drop = this.querySelector<HTMLElement>("[data-photo-drop]");
    if (drop) {
      for (const type of ["dragenter", "dragover"]) {
        drop.addEventListener(type, (e: Event): void => {
          e.preventDefault();
          drop.classList.add("is-dragging");
        });
      }
      for (const type of ["dragleave", "drop"]) {
        drop.addEventListener(type, (): void => drop.classList.remove("is-dragging"));
      }
      drop.addEventListener("drop", (e: DragEvent): void => {
        e.preventDefault();
        this.add(Array.from(e.dataTransfer?.files ?? []));
      });
    }
  }

  private add(incoming: File[]): void {
    const rejected: string[] = [];

    for (const file of incoming) {
      if (this.files.length >= MAX_FILES) {
        rejected.push(`sólo puedes subir ${MAX_FILES} fotos`);
        break;
      }
      if (!file.type.startsWith("image/")) {
        rejected.push(`${file.name} no es una imagen`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        rejected.push(`${file.name} pesa más de 8 MB`);
        continue;
      }
      // Same photo picked twice (easy to do across two "Elegir" clicks).
      const duplicate = this.files.some(
        (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
      );
      if (!duplicate) this.files.push(file);
    }

    this.sync();
    this.render(rejected);
  }

  private removeAt(index: number): void {
    this.files.splice(index, 1);
    this.sync();
    this.render([]);
  }

  /**
   * Writes our list back into the <input>, which is what actually gets submitted.
   * input.files is a read-only FileList, so it can only be replaced wholesale via
   * a DataTransfer — that is the only way to drop a single file from the picker.
   */
  private sync(): void {
    const dt = new DataTransfer();
    for (const f of this.files) dt.items.add(f);
    this.input.files = dt.files;
  }

  private render(rejected: string[]): void {
    this.grid.replaceChildren();

    for (const [index, file] of this.files.entries()) {
      const url = URL.createObjectURL(file);

      const item = document.createElement("figure");
      item.className = "photo-picker__item";

      const img = document.createElement("img");
      img.src = url;
      img.alt = file.name;
      img.loading = "lazy";
      // The blob URL pins the file in memory until it is revoked.
      img.addEventListener("load", (): void => URL.revokeObjectURL(url), { once: true });

      const remove = document.createElement("button");
      remove.type = "button"; // never submit the form
      remove.className = "photo-picker__remove";
      remove.setAttribute("aria-label", `Quitar ${file.name}`);
      remove.textContent = "✕";
      remove.addEventListener("click", (): void => this.removeAt(index));

      const caption = document.createElement("figcaption");
      caption.textContent = kb(file.size);

      item.append(img, remove, caption);
      this.grid.append(item);
    }

    const parts: string[] = [];
    if (this.files.length > 0) {
      parts.push(`${this.files.length} de ${MAX_FILES} fotos · toca ✕ para quitar una.`);
    }
    if (rejected.length > 0) {
      parts.push(`No agregamos: ${rejected.join(", ")}.`);
    }
    this.note.textContent = parts.join(" ");
    this.note.dataset["state"] = rejected.length > 0 ? "error" : "ok";
  }
}

function kb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}
